import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Alert, Button, Skeleton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type {
  CallRecordListItem,
  CallRecordsSortField,
  CallUrgency,
  CallType,
  CallOutcome,
} from '@techgitter/contracts';
import {
  SecondaryToolbar,
  ToolbarSearch,
  ToolbarSelect,
  ToolbarCheckbox,
  ToolbarDate,
  ToolbarSpacer,
  ToolbarCount,
} from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
  DataTablePagination,
} from '../components/table';
import { Chip } from '../components/chip';
import type { ChipTone } from '../components/chip';
import { fetchCalls } from '../services/callsApi';
import { CallDirectionIcon } from '../components/calls/CallDirectionIcon';
import { formatCallTime } from '../utils/callFormat';

const URGENCY_OPTIONS = [
  { value: '', label: 'Any urgency' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

// Short labels — the column has to stay scannable, the detail page spells it out.
const CALL_TYPE_LABELS: Record<CallType, string> = {
  general_enquiry: 'Enquiry',
  quote_request: 'Quote',
  onsite_booking: 'Onsite',
  callback_return: 'Callback',
  prospective_business: 'Prospect',
  existing_job: 'Existing job',
  supplier_or_sales: 'Supplier',
  other: 'Other',
};

const OUTCOME_LABELS: Record<CallOutcome, string> = {
  resolved: 'Resolved',
  message_taken: 'Message taken',
  message_left: 'Message left',
  transferred: 'Transferred',
  no_action: 'No action',
};

// `message_taken` is the one that means somebody here still has to act, so it
// is the only outcome that earns a colour that draws the eye.
const OUTCOME_TONES: Record<CallOutcome, ChipTone> = {
  resolved: 'success',
  message_taken: 'warning',
  message_left: 'info',
  transferred: 'neutral',
  no_action: 'neutral',
};

const CALL_TYPE_OPTIONS = [
  { value: '', label: 'Any type' },
  ...(Object.keys(CALL_TYPE_LABELS) as CallType[]).map((v) => ({
    value: v, label: CALL_TYPE_LABELS[v],
  })),
];

const OUTCOME_OPTIONS = [
  { value: '', label: 'Any outcome' },
  ...(Object.keys(OUTCOME_LABELS) as CallOutcome[]).map((v) => ({
    value: v, label: OUTCOME_LABELS[v],
  })),
];

const columnLabels: Record<CallRecordsSortField, string> = {
  startTime: 'Time',
  durationSecs: 'Duration',
  customerName: 'Customer',
};

/** `13 Sep 10:22` in the shop's own timezone — call times are local facts. */
const formatDuration = (secs: number | null): string => {
  if (secs === null || secs === undefined) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/** `+61412345678` → `0412 345 678`; anything unexpected passes through. */
const formatNumber = (value: string | null): string => {
  if (!value) return '—';
  const au = value.match(/^\+61(\d{9})$/);
  if (!au) return value;
  const d = au[1];
  return d.startsWith('4')
    ? `0${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`
    : `0${d.slice(0, 1)} ${d.slice(1, 5)} ${d.slice(5)}`;
};

const urgencyTone = (urgency: CallUrgency | null): ChipTone => {
  if (urgency === 'emergency') return 'error';
  if (urgency === 'high') return 'warning';
  return 'neutral';
};

/**
 * The compact flag cluster. Deliberately terse — the detail page explains
 * each one; here they just have to be scannable down a column.
 */
const FlagChips: React.FC<{ call: CallRecordListItem }> = ({ call }) => {
  const flags: React.ReactNode[] = [];
  if (call.reviewStatus === 'open') {
    flags.push(<Chip key="review" tone="warning">Review</Chip>);
  }
  if (call.callbackRequired) {
    flags.push(<Chip key="callback" tone="info">Callback</Chip>);
  }
  if (call.credentialReview) {
    flags.push(<Chip key="cred" tone="error">Credential</Chip>);
  }
  if (call.hasOutstanding) {
    flags.push(<Chip key="outstanding" tone="neutral">Outstanding</Chip>);
  }
  if (flags.length === 0) return <Box sx={{ color: 'text.disabled' }}>—</Box>;
  return <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{flags}</Box>;
};

interface UserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];
}

interface CallsPageProps {
  userInfo?: UserInfo | null;
}

export const CallsPage: React.FC<CallsPageProps> = () => {
  const navigate = useNavigate();

  // Pagination. Dataverse has no `$skip`, so the API pages with a forward
  // cursor rather than a page number (see the contract note on
  // `CallRecordListQuery.cursor`). The footer still shows a 0-based page
  // index, so we keep the cursors we've been handed: cursorsRef.current[n] is
  // the cursor that fetches page n, and page 0's is always undefined. That
  // supports next/previous — which is all DataTablePagination offers — but
  // deliberately not jumping to an arbitrary page.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const cursorsRef = useRef<(string | undefined)[]>([undefined]);

  const resetPaging = useCallback(() => {
    cursorsRef.current = [undefined];
    setPage(0);
  }, []);

  // Sort state
  const [sort, setSort] = useState<CallRecordsSortField>('startTime');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Filter state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [urgency, setUrgency] = useState('');
  const [callType, setCallType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const [callbackOnly, setCallbackOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // Data state
  const [items, setItems] = useState<CallRecordListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Debounce the free-text search so every keystroke doesn't fire a server
  // call. 300ms matches InvoicesPage / CustomersPage.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter / sort change resets to page 0 so the user lands on the start
  // of the new result set. The cursors go with it — a paging cookie is only
  // valid for the query that issued it.
  useEffect(() => {
    resetPaging();
  }, [debouncedSearch, urgency, callType, outcome, needsReview, callbackOnly, mineOnly,
      from, to, sort, order, resetPaging]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    fetchCalls({
      cursor: cursorsRef.current[page],
      pageSize: rowsPerPage,
      sort,
      order,
      search: debouncedSearch || undefined,
      from: from || undefined,
      to: to || undefined,
      urgency: (urgency as CallUrgency) || undefined,
      callType: (callType as CallType) || undefined,
      outcome: (outcome as CallOutcome) || undefined,
      reviewStatus: needsReview ? 'open' : undefined,
      callbackRequired: callbackOnly || undefined,
      mine: mineOnly || undefined,
      signal: ctrl.signal,
    })
      .then((res) => {
        setItems(res.items);
        setTotalItems(res.pagination.totalItems);
        // Remember how to reach the next page. Recorded against page+1 so a
        // later Next has the cursor it needs without another round trip.
        if (res.pagination.nextCursor) {
          cursorsRef.current[page + 1] = res.pagination.nextCursor;
        }
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [
    debouncedSearch,
    urgency,
    callType,
    outcome,
    needsReview,
    callbackOnly,
    mineOnly,
    from,
    to,
    page,
    rowsPerPage,
    sort,
    order,
    retryNonce,
  ]);

  const handleSortClick = useCallback(
    (field: CallRecordsSortField) => {
      if (sort === field) {
        setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSort(field);
        setOrder('desc');
      }
    },
    [sort],
  );

  const handleRetry = () => setRetryNonce((n) => n + 1);

  const showingStart = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const showingEnd = Math.min(totalItems, (page + 1) * rowsPerPage);

  const renderCustomer = (call: CallRecordListItem) => {
    // An unmatched / ambiguous caller is the single most actionable thing on
    // the row, so it reads as a chip rather than an empty cell.
    if (!call.customerId) {
      return (
        <Chip tone={call.matchStatus === 'ambiguous' ? 'warning' : 'neutral'}>
          {call.matchStatus === 'ambiguous' ? 'Ambiguous' : 'Unmatched'}
        </Chip>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <span>{call.customerName || '—'}</span>
        {call.matchStatus === 'manual' && <Chip tone="info">Manual</Chip>}
      </Box>
    );
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSearch
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search customer, number, summary or work unit…"
          maxWidth={480}
        />
        <ToolbarSelect
          label="Urgency"
          value={urgency}
          onChange={setUrgency}
          options={URGENCY_OPTIONS}
        />
        <ToolbarSelect
          label="Type"
          value={callType}
          onChange={setCallType}
          options={CALL_TYPE_OPTIONS}
        />
        <ToolbarSelect
          label="Outcome"
          value={outcome}
          onChange={setOutcome}
          options={OUTCOME_OPTIONS}
        />
        <ToolbarCheckbox
          label="Needs review"
          active={needsReview}
          onClick={() => setNeedsReview((v) => !v)}
        />
        <ToolbarCheckbox
          label="Callback"
          active={callbackOnly}
          onClick={() => setCallbackOnly((v) => !v)}
        />
        <ToolbarCheckbox
          label="My calls"
          active={mineOnly}
          onClick={() => setMineOnly((v) => !v)}
        />
        <ToolbarDate label="From" value={from} onChange={setFrom} />
        <ToolbarDate label="To" value={to} onChange={setTo} />
        <ToolbarSpacer />
        <ToolbarCount>
          {totalItems === 0
            ? 'No results'
            : `Showing ${showingStart}–${showingEnd} of ${totalItems}`}
        </ToolbarCount>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px', maxWidth: 1600, mx: 'auto' }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell
                  sortable
                  active={sort === 'startTime'}
                  direction={order}
                  onSort={() => handleSortClick('startTime')}
                >
                  {columnLabels.startTime}
                </DataTableHeadCell>
                <DataTableHeadCell>Dir</DataTableHeadCell>
                <DataTableHeadCell
                  sortable
                  active={sort === 'durationSecs'}
                  direction={order}
                  onSort={() => handleSortClick('durationSecs')}
                >
                  {columnLabels.durationSecs}
                </DataTableHeadCell>
                <DataTableHeadCell>Number</DataTableHeadCell>
                <DataTableHeadCell
                  sortable
                  active={sort === 'customerName'}
                  direction={order}
                  onSort={() => handleSortClick('customerName')}
                >
                  {columnLabels.customerName}
                </DataTableHeadCell>
                <DataTableHeadCell>Job</DataTableHeadCell>
                <DataTableHeadCell>Staff</DataTableHeadCell>
                <DataTableHeadCell>Type</DataTableHeadCell>
                <DataTableHeadCell>Outcome</DataTableHeadCell>
                <DataTableHeadCell>Urgency</DataTableHeadCell>
                <DataTableHeadCell>Flags</DataTableHeadCell>
                <DataTableHeadCell>Summary</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <DataTableRow key={`skeleton-${i}`}>
                    {Array.from({ length: 12 }).map((__, j) => (
                      <DataTableCell key={j}>
                        <Skeleton variant="text" />
                      </DataTableCell>
                    ))}
                  </DataTableRow>
                ))}
              {!loading && items.length === 0 && !error && (
                <DataTableEmpty colSpan={12}>No calls found</DataTableEmpty>
              )}
              {!loading &&
                items.map((call) => {
                  const summary = call.operationalSummaryText || '';
                  const truncated =
                    summary.length > 70 ? `${summary.slice(0, 70)}…` : summary;
                  return (
                    <DataTableRow
                      key={call.id}
                      onClick={() => navigate(`/call/${call.id}`)}
                    >
                      <DataTableCell bold>
                        {formatCallTime(call.startTime)}
                      </DataTableCell>
                      <DataTableCell>
                        <CallDirectionIcon call={call} />
                      </DataTableCell>
                      <DataTableCell>
                        {formatDuration(call.durationSecs)}
                      </DataTableCell>
                      <DataTableCell>{formatNumber(call.number)}</DataTableCell>
                      <DataTableCell>{renderCustomer(call)}</DataTableCell>
                      <DataTableCell>{call.jobNumber || '—'}</DataTableCell>
                      <DataTableCell>{call.userFullName || '—'}</DataTableCell>
                      <DataTableCell>
                        {call.callType ? (
                          <Chip tone={call.callType === 'prospective_business' ? 'info' : 'neutral'}>
                            {CALL_TYPE_LABELS[call.callType]}
                          </Chip>
                        ) : (
                          <Box sx={{ color: 'text.disabled' }}>—</Box>
                        )}
                      </DataTableCell>
                      <DataTableCell>
                        {call.outcome ? (
                          <Chip tone={OUTCOME_TONES[call.outcome]}>
                            {OUTCOME_LABELS[call.outcome]}
                          </Chip>
                        ) : (
                          <Box sx={{ color: 'text.disabled' }}>—</Box>
                        )}
                      </DataTableCell>
                      <DataTableCell>
                        {call.urgency ? (
                          <Chip tone={urgencyTone(call.urgency)}>
                            {call.urgency}
                          </Chip>
                        ) : (
                          '—'
                        )}
                      </DataTableCell>
                      <DataTableCell>
                        <FlagChips call={call} />
                      </DataTableCell>
                      <DataTableCell>
                        {summary.length > 70 ? (
                          <Tooltip title={summary}>
                            <span>{truncated}</span>
                          </Tooltip>
                        ) : (
                          truncated || '—'
                        )}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
            </tbody>
          </DataTable>
          <DataTablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            count={totalItems}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(next) => {
              // Forward-only cursors: step to a page we hold a cursor for.
              // Page 0 always qualifies (no cursor needed).
              if (next === 0 || cursorsRef.current[next] !== undefined) {
                setPage(next);
              }
            }}
            onRowsPerPageChange={(rpp) => {
              // A cursor is tied to the page size it was issued with.
              setRowsPerPage(rpp);
              resetPaging();
            }}
          />
        </DataTableCard>
      </Box>
    </>
  );
};
